import React from 'react';
import {
  formatPrice,
  formatChange,
  formatChangeAmount,
  ouncesToGrams,
} from '../utils/formatters';

const PriceTable = ({ data, country }) => {
  if (!data || !country) return null;

  const calculatedChangePercent = data.ch_pct || (data.ch && data.price ? (data.ch / (data.price - data.ch)) * 100 : 0);
  const isPositive = data.ch >= 0;

  const karatRows = [
    { label: 'عيار 24', buyPrice: data.price_gram_24k, sellPrice: data.price_gram_24k * 1.002, karat: '24' },
    { label: 'عيار 22', buyPrice: data.price_gram_22k, sellPrice: data.price_gram_22k * 1.002, karat: '22' },
    { label: 'عيار 21', buyPrice: data.price_gram_21k, sellPrice: data.price_gram_21k * 1.002, karat: '21' },
    { label: 'عيار 18', buyPrice: data.price_gram_18k, sellPrice: data.price_gram_18k * 1.002, karat: '18' },
  ].map(row => ({
    ...row,
    change: (data.ch / 31.1035) * (parseInt(row.karat) / 24),
    changePercent: calculatedChangePercent,
    isPositive: isPositive
  }));

  // Ounce row
  const ounceRow = {
    label: 'أوقية الذهب',
    sellPrice: data.price * 1.002,
    buyPrice: data.price,
    change: data.ch,
    changePercent: calculatedChangePercent,
    isPositive: isPositive,
  };

  // Gold Coin row (8 grams of 21k gold)
  const coinRow = {
    label: 'جنيه الذهب (8ج عيار 21)',
    sellPrice: (data.price_gram_21k * 1.002) * 8,
    buyPrice: data.price_gram_21k * 8,
    change: ((data.ch / 31.1035) * (21 / 24)) * 8,
    changePercent: calculatedChangePercent,
    isPositive: isPositive,
  };

  return (
    <div className="price-table-wrapper">
      {/* Summary Card */}
      <div className="summary-card" style={{ justifyContent: 'center', textAlign: 'center', padding: '40px 20px' }}>
        <div className="summary-info-centered">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '15px', marginBottom: '15px' }}>
            <span className="summary-flag" style={{ fontSize: '2.5rem' }}>{country.flag}</span>
            <h1 className="summary-country" style={{ fontSize: '2rem', fontWeight: '850', margin: 0 }}>سعر الذهب اليوم في {country.name}</h1>
          </div>
          <div className="summary-subinfo" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '15px' }}>
            <span className="summary-currency" style={{ fontSize: '1rem', color: 'var(--color-text-secondary)', fontWeight: '600' }}>{country.currencyName}</span>
            <span className="summary-date-badge" style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '6px 14px', backgroundColor: 'rgba(212, 168, 67, 0.1)',
              borderRadius: 'var(--radius-full)', fontSize: '0.9rem',
              color: 'var(--color-gold-dark)', fontWeight: '700',
              fontFamily: 'monospace', border: '1px solid rgba(212, 168, 67, 0.2)'
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="16" y1="2" x2="16" y2="6"></line>
                <line x1="8" y1="2" x2="8" y2="6"></line>
                <line x1="3" y1="10" x2="21" y2="10"></line>
              </svg>
              {new Date((data.timestamp < 10000000000 ? data.timestamp * 1000 : data.timestamp) || Date.now()).toLocaleDateString('en-GB')}
            </span>
          </div>
        </div>
      </div>

      {/* Price Table */}
      <div className="price-table-container">
        <table className="price-table" id="gold-price-table">
          <thead>
            <tr>
              <th className="th-karat">العيار</th>
              <th className="th-sell">
                <span className="help-icon">
                  ?
                  <span className="tooltip-text">ده السعر اللي الصايغ بيبيعلك بيه الذهب (لما تروح تشتري منه)</span>
                </span>
                أنت تشتري
              </th>
              <th className="th-buy">
                <span className="help-icon">
                  ?
                  <span className="tooltip-text">ده السعر اللي الصايغ بيشتري بيه الذهب منك (لما تروح تبيعله)</span>
                </span>
                أنت تبيع
              </th>
              <th className="th-change">
                <span className="help-icon">
                  ?
                  <span className="tooltip-text">مقدار الزيادة أو النقصان في السعر مقارنة بآخر إغلاق</span>
                </span>
                التغيير
              </th>
              <th className="th-pct">
                <span className="help-icon">
                  ?
                  <span className="tooltip-text">نسبة التغير المئوية في السعر (سواء زاد أو قل)</span>
                </span>
                النسبة %
              </th>
            </tr>
          </thead>
          <tbody>
            {karatRows.map((row) => (
              <tr key={row.karat} className="price-row">
                <td className="td-karat">
                  <span className="karat-dot"></span>
                  {row.label}
                </td>
                <td className="td-price">
                  {formatPrice(row.sellPrice, country.currencySymbol)}
                </td>
                <td className="td-price">
                  {formatPrice(row.buyPrice, country.currencySymbol)}
                </td>
                <td className="td-change">
                  <span className={`change-badge ${row.isPositive ? 'positive' : 'negative'}`}>
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                      {row.isPositive ? (
                        <path d="M5 2L8 7H2L5 2Z" fill="currentColor" />
                      ) : (
                        <path d="M5 8L2 3H8L5 8Z" fill="currentColor" />
                      )}
                    </svg>
                    {formatChangeAmount(row.change, country.currencySymbol)}
                  </span>
                </td>
                <td className="td-pct">
                  <span className={`change-badge ${row.isPositive ? 'positive' : 'negative'}`}>
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                      {row.isPositive ? (
                        <path d="M5 2L8 7H2L5 2Z" fill="currentColor" />
                      ) : (
                        <path d="M5 8L2 3H8L5 8Z" fill="currentColor" />
                      )}
                    </svg>
                    {formatChange(row.changePercent)}
                  </span>
                </td>
              </tr>
            ))}
            {/* Coin Row (Only for Egypt) */}
            {country.id === 'egypt' && (
              <tr className="price-row coin-row">
                <td className="td-karat">
                  <span className="karat-dot coin-dot" style={{ backgroundColor: '#F59E0B' }}></span>
                  {coinRow.label}
                </td>
                <td className="td-price">
                  {formatPrice(coinRow.sellPrice, country.currencySymbol)}
                </td>
                <td className="td-price">
                  {formatPrice(coinRow.buyPrice, country.currencySymbol)}
                </td>
                <td className="td-change">
                  <span className={`change-badge ${coinRow.isPositive ? 'positive' : 'negative'}`}>
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                      {coinRow.isPositive ? (
                        <path d="M5 2L8 7H2L5 2Z" fill="currentColor" />
                      ) : (
                        <path d="M5 8L2 3H8L5 8Z" fill="currentColor" />
                      )}
                    </svg>
                    {formatChangeAmount(coinRow.change, country.currencySymbol)}
                  </span>
                </td>
                <td className="td-pct">
                  <span className={`change-badge ${coinRow.isPositive ? 'positive' : 'negative'}`}>
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                      {coinRow.isPositive ? (
                        <path d="M5 2L8 7H2L5 2Z" fill="currentColor" />
                      ) : (
                        <path d="M5 8L2 3H8L5 8Z" fill="currentColor" />
                      )}
                    </svg>
                    {formatChange(coinRow.changePercent)}
                  </span>
                </td>
              </tr>
            )}
            {/* Ounce Row */}
            <tr className="price-row ounce-row">
              <td className="td-karat">
                <span className="karat-dot ounce-dot"></span>
                {ounceRow.label}
              </td>
              <td className="td-price">
                {formatPrice(ounceRow.sellPrice, country.currencySymbol)}
              </td>
              <td className="td-price">
                {formatPrice(ounceRow.buyPrice, country.currencySymbol)}
              </td>
              <td className="td-change">
                <span className={`change-badge ${ounceRow.isPositive ? 'positive' : 'negative'}`}>
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                    {ounceRow.isPositive ? (
                      <path d="M5 2L8 7H2L5 2Z" fill="currentColor" />
                    ) : (
                      <path d="M5 8L2 3H8L5 8Z" fill="currentColor" />
                    )}
                  </svg>
                  {formatChangeAmount(ounceRow.change, country.currencySymbol)}
                </span>
              </td>
              <td className="td-pct">
                <span className={`change-badge ${ounceRow.isPositive ? 'positive' : 'negative'}`}>
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                    {ounceRow.isPositive ? (
                      <path d="M5 2L8 7H2L5 2Z" fill="currentColor" />
                    ) : (
                      <path d="M5 8L2 3H8L5 8Z" fill="currentColor" />
                    )}
                  </svg>
                  {formatChange(ounceRow.changePercent)}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Mobile Price Cards */}
      <div className="mobile-price-cards">
        {karatRows.map((row) => (
          <div key={row.karat} className="price-card">
            <div className="card-header">
              <div className="card-karat">
                <span className="karat-dot"></span>
                <strong>{row.label}</strong>
              </div>
              <div className={`card-change ${row.isPositive ? 'positive' : 'negative'}`}>
                {formatChange(row.changePercent)}
              </div>
            </div>
            <div className="card-body">
              <div className="price-item">
                <span className="price-label">انت تشتري</span>
                <span className="price-value">{formatPrice(row.sellPrice, country.currencySymbol)}</span>
              </div>
              <div className="price-divider"></div>
              <div className="price-item">
                <span className="price-label">انت تبيع</span>
                <span className="price-value">{formatPrice(row.buyPrice, country.currencySymbol)}</span>
              </div>
            </div>
          </div>
        ))}

        {/* Coin Card (Only for Egypt) */}
        {country.id === 'egypt' && (
          <div className="price-card coin-card">
            <div className="card-header">
              <div className="card-karat">
                <span className="karat-dot coin-dot" style={{ backgroundColor: '#F59E0B' }}></span>
                <strong>{coinRow.label}</strong>
              </div>
              <div className={`card-change ${coinRow.isPositive ? 'positive' : 'negative'}`}>
                {formatChange(coinRow.changePercent)}
              </div>
            </div>
            <div className="card-body">
              <div className="price-item">
                <span className="price-label">بيع</span>
                <span className="price-value">{formatPrice(coinRow.sellPrice, country.currencySymbol)}</span>
              </div>
              <div className="price-divider"></div>
              <div className="price-item">
                <span className="price-label">شراء</span>
                <span className="price-value">{formatPrice(coinRow.buyPrice, country.currencySymbol)}</span>
              </div>
            </div>
          </div>
        )}

        {/* Ounce Card */}
        <div className="price-card ounce-card">
          <div className="card-header">
            <div className="card-karat">
              <span className="karat-dot ounce-dot"></span>
              <strong>أوقية الذهب</strong>
            </div>
            <div className={`card-change ${ounceRow.isPositive ? 'positive' : 'negative'}`}>
              {formatChange(ounceRow.changePercent)}
            </div>
          </div>
          <div className="card-body">
            <div className="price-item">
              <span className="price-label">بيع</span>
              <span className="price-value">{formatPrice(ounceRow.sellPrice, country.currencySymbol)}</span>
            </div>
            <div className="price-divider"></div>
            <div className="price-item">
              <span className="price-label">شراء</span>
              <span className="price-value">{formatPrice(ounceRow.buyPrice, country.currencySymbol)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PriceTable;
